import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bot,
  Calendar,
  Check,
  Code2,
  ArrowRight,
  Zap,
  MessageSquareText,
  Instagram,
  Facebook,
  Mail as MailIcon,
} from "lucide-react";

const SERVICES = [
  {
    id: "websites",
    icon: Code2,
    title: "websites",
    tagline: "conversion-first sites your customers actually finish reading.",
    bullets: [
      "custom design tuned to your brand",
      "seo, page-speed, and analytics baked in",
      "booking + payments on the same page",
      "cms so you can update copy in minutes",
    ],
    cta: "get a website",
  },
  {
    id: "chatbots",
    icon: Bot,
    title: "ai chatbots",
    tagline: "an ai receptionist trained on your business, live in 7 days.",
    bullets: [
      "answers pricing, hours, and faq instantly",
      "qualifies leads and books meetings",
      "hands off to a human when it matters",
      "learns from every conversation",
    ],
    cta: "build my chatbot",
  },
  {
    id: "automations",
    icon: Zap,
    title: "automations",
    tagline: "whatsapp, instagram, facebook, email — one pipeline that runs itself.",
    bullets: [
      "auto-reply on whatsapp business, instagram, facebook",
      "reminders, follow-ups, review requests",
      "sync to google calendar, notion, sheets",
      "abandoned lead recovery in 3 messages",
    ],
    cta: "automate my dms",
    channels: true,
  },
  {
    id: "bookings",
    icon: Calendar,
    title: "bookings & reservations",
    tagline: "self-serve slot booking on your site, dms, or website chat.",
    bullets: [
      "real-time availability, no double-bookings",
      "auto reminders 24h and 2h before",
      "google calendar sync",
      "cancellation & reschedule links",
    ],
    cta: "add booking to my site",
  },
];

export default function Services() {
  return (
    <div className="bg-section-glow" data-testid="services-page">
      <section className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <h1 className="font-display lowercase font-bold text-5xl sm:text-6xl tracking-tighter" data-testid="services-heading">
            everything you need to <span className="text-blue-500">sell online.</span>
          </h1>
          <p className="mt-5 text-gray-400 max-w-2xl">
            pick one — or all four. each service is built to plug into the next,
            so your website, chatbot and crm behave like one revenue engine.
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 space-y-10">
        {SERVICES.map((s, i) => (
          <motion.section
            key={s.id}
            id={s.id}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5 }}
            className="kyv-card p-8 lg:p-12"
            data-testid={`service-section-${s.id}`}
          >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <div className="p-3 rounded-full bg-blue-500/10 text-blue-400 w-fit mb-5">
                  <s.icon size={22} />
                </div>
                <h2 className="font-display lowercase font-bold text-3xl sm:text-4xl tracking-tight">
                  {s.title}
                </h2>
                <p className="mt-3 text-gray-400 max-w-xl">{s.tagline}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {s.bullets.map((b, bi) => (
                    <li key={bi} className="flex items-start gap-3">
                      <Check size={16} className="text-blue-400 mt-1 shrink-0" />
                      <span className="text-sm text-gray-200">{b}</span>
                    </li>
                  ))}
                </ul>
                {s.channels && (
                  <div className="mt-6 flex flex-wrap gap-2">
                    {[
                      { icon: MessageSquareText, label: "whatsapp" },
                      { icon: Instagram, label: "instagram" },
                      { icon: Facebook, label: "facebook" },
                      { icon: MailIcon, label: "email" },
                    ].map((c, ci) => (
                      <span key={ci} className="kyv-badge">
                        <c.icon size={12} /> {c.label}
                      </span>
                    ))}
                  </div>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contact#book" className="kyv-btn-primary inline-flex items-center gap-2" data-testid={`service-cta-${s.id}`}>
                    {s.cta} <ArrowRight size={16} />
                  </Link>
                  <Link to={`/contact#contact`} className="kyv-btn-ghost">
                    ask a question
                  </Link>
                </div>
              </div>
              <div className="lg:col-span-2">
                <div className="kyv-card p-6 space-y-3">
                  <p className="text-xs text-blue-400 uppercase tracking-widest">outcome</p>
                  <p className="font-display lowercase font-bold text-2xl leading-tight">
                    more replies, more bookings, more revenue — without hiring.
                  </p>
                  <p className="text-sm text-gray-400">
                    typical clients see 2–3× faster response times and 30–40%
                    more booked meetings within the first 30 days.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        ))}
      </div>
    </div>
  );
}
